/**
 * Pure utility functions for Timesheet Overview page.
 * No React dependencies — all functions are stateless and testable.
 */

/**
 * Returns the Monday (ISO week start) for a given date as YYYY-MM-DD string.
 * @param {Date|string} date
 * @returns {string} YYYY-MM-DD
 */
export function getISOWeekMonday(date) {
  const d = new Date(typeof date === 'string' ? date + 'T00:00:00' : date);
  const day = d.getDay(); // 0=Sun, 1=Mon...6=Sat
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  d.setDate(d.getDate() + diff);
  return formatDate(d);
}

/**
 * Returns an array of 7 YYYY-MM-DD strings (Mon through Sun) for a given week.
 * @param {string} mondayStr YYYY-MM-DD (must be a Monday)
 * @returns {string[]}
 */
export function getISOWeekDates(mondayStr) {
  const dates = [];
  const d = new Date(mondayStr + 'T00:00:00');
  for (let i = 0; i < 7; i++) {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    dates.push(formatDate(day));
  }
  return dates;
}

/**
 * Returns an array of ISO week-start Mondays that overlap a given month.
 * @param {number} year
 * @param {number} month 0-indexed (0=Jan)
 * @returns {string[]} array of YYYY-MM-DD Monday strings
 */
export function getWeeksInMonth(year, month) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const firstMonday = getISOWeekMonday(firstDay);
  const lastMonday = getISOWeekMonday(lastDay);

  const weeks = [];
  const current = new Date(firstMonday + 'T00:00:00');
  const end = new Date(lastMonday + 'T00:00:00');

  while (current <= end) {
    weeks.push(formatDate(current));
    current.setDate(current.getDate() + 7);
  }
  return weeks;
}

/**
 * Returns the ISO week number for a given date.
 * @param {Date|string} date
 * @returns {number}
 */
export function getISOWeekNumber(date) {
  const d = new Date(typeof date === 'string' ? date + 'T00:00:00' : date);
  const temp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = temp.getUTCDay() || 7;
  temp.setUTCDate(temp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(temp.getUTCFullYear(), 0, 1));
  return Math.ceil(((temp - yearStart) / 86400000 + 1) / 7);
}

/**
 * Counts working days (Mon-Fri) in a date range, excluding holidays.
 * @param {string} startStr YYYY-MM-DD
 * @param {string} endStr YYYY-MM-DD
 * @param {Set<string>} holidaySet Set of YYYY-MM-DD holiday dates
 * @returns {number}
 */
export function getWorkingDaysInRange(startStr, endStr, holidaySet) {
  let count = 0;
  const current = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  while (current <= end) {
    const day = current.getDay();
    if (day >= 1 && day <= 5 && !holidaySet.has(formatDate(current))) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
}

/**
 * Returns a Set of YYYY-MM-DD dates where a user has approved time-off.
 * Expands each time-off range into individual dates.
 * @param {Array} timeOffRecords filtered for one user
 * @returns {Set<string>}
 */
export function getUserTimeOffDates(timeOffRecords) {
  const dates = new Set();
  for (const record of timeOffRecords) {
    if (!record.startDate || !record.endDate) continue;
    const current = new Date(record.startDate + 'T00:00:00');
    const end = new Date(record.endDate + 'T00:00:00');
    while (current <= end) {
      const day = current.getDay();
      if (day >= 1 && day <= 5) {
        dates.add(formatDate(current));
      }
      current.setDate(current.getDate() + 1);
    }
  }
  return dates;
}

/**
 * Calculates prorated weekly contracted hours for a user in a given week.
 * Prorates for: join date, end date, holidays, and approved time-off.
 * This gives the realistic number of hours the user is expected to log.
 * @param {object} user { contractedHours, joinDate, endDate }
 * @param {string} weekMonday YYYY-MM-DD
 * @param {Set<string>} holidaySet
 * @param {Set<string>} timeOffDates
 * @returns {number} prorated hours
 */
export function prorateWeeklyContracted(user, weekMonday, holidaySet, timeOffDates) {
  const weeklyContracted = (user.contractedHours ?? 0) * 12 / 52;
  const weekDates = getISOWeekDates(weekMonday);

  // Count total eligible weekdays (Mon-Fri within join/end bounds)
  let totalDays = 0;
  let leaveDays = 0;
  let holidayDays = 0;
  for (let i = 0; i < 5; i++) { // Mon-Fri only
    const dateStr = weekDates[i];
    if (user.joinDate && dateStr < user.joinDate) continue;
    if (user.endDate && dateStr > user.endDate) continue;
    totalDays++;
    if (holidaySet.has(dateStr)) holidayDays++;
    else if (timeOffDates.has(dateStr)) leaveDays++;
  }

  if (totalDays === 0) return 0;
  // Target = contracted hours prorated for join/end, minus holiday and leave hours.
  const proratedForJoinEnd = weeklyContracted * (totalDays / 5);
  const dailyRate = totalDays > 0 ? proratedForJoinEnd / totalDays : 0;
  return proratedForJoinEnd - ((holidayDays + leaveDays) * dailyRate);
}

/**
 * Calculates prorated monthly contracted hours for a user.
 * Prorates for: join date, end date, holidays, and approved time-off.
 * This gives the realistic number of hours the user is expected to log.
 * @param {object} user { contractedHours, joinDate, endDate }
 * @param {number} year
 * @param {number} month 0-indexed
 * @param {Set<string>} holidaySet
 * @param {Set<string>} timeOffDates
 * @returns {number}
 */
export function prorateMonthlyContracted(user, year, month, holidaySet, timeOffDates) {
  const monthlyContracted = user.contractedHours ?? 0;
  const firstDay = formatDate(new Date(year, month, 1));
  const lastDay = formatDate(new Date(year, month + 1, 0));
  const totalWorkingDays = getWorkingDaysInRange(firstDay, lastDay, new Set());

  if (totalWorkingDays === 0) return 0;

  // Count eligible weekdays (within join/end bounds), holidays, and leave days.
  let eligibleDays = 0;
  let leaveDays = 0;
  let holidayDays = 0;
  const current = new Date(firstDay + 'T00:00:00');
  const end = new Date(lastDay + 'T00:00:00');
  while (current <= end) {
    const dateStr = formatDate(current);
    const day = current.getDay();
    if (day >= 1 && day <= 5) {
      const inBounds = (!user.joinDate || dateStr >= user.joinDate)
        && (!user.endDate || dateStr <= user.endDate);
      if (inBounds) {
        eligibleDays++;
        if (holidaySet.has(dateStr)) holidayDays++;
        else if (timeOffDates.has(dateStr)) leaveDays++;
      }
    }
    current.setDate(current.getDate() + 1);
  }

  // Prorate for join/end dates, then subtract holiday and leave hours
  const proratedForJoinEnd = monthlyContracted * (eligibleDays / totalWorkingDays);
  const dailyRate = eligibleDays > 0 ? proratedForJoinEnd / eligibleDays : 0;
  return proratedForJoinEnd - ((holidayDays + leaveDays) * dailyRate);
}

/**
 * Fraction (0..1] of a month's working days (Mon-Fri) the user was employed,
 * based on join/end dates. Used to pro-rate a mid-month joiner/leaver's salary
 * cost for their first / last month. Mirrors the join/end day-count in
 * prorateMonthlyContracted. A full month — or a month with no join/end
 * constraint — returns 1; a month entirely outside employment returns 0.
 * Holidays/leave are intentionally NOT considered here (the cost path deducts
 * those separately via getEffectiveContractedHours).
 * @param {string|null} joinDate YYYY-MM-DD (or null/undefined)
 * @param {string|null} endDate  YYYY-MM-DD (or null/undefined)
 * @param {number} year
 * @param {number} month 1-indexed (1 = January)
 * @returns {number}
 */
export function employedMonthFraction(joinDate, endDate, year, month) {
  const m0 = month - 1;
  const firstDay = formatDate(new Date(year, m0, 1));
  const lastDay = formatDate(new Date(year, m0 + 1, 0));
  const totalWorkingDays = getWorkingDaysInRange(firstDay, lastDay, new Set());
  if (totalWorkingDays === 0) return 1;

  let eligibleDays = 0;
  const current = new Date(firstDay + 'T00:00:00');
  const end = new Date(lastDay + 'T00:00:00');
  while (current <= end) {
    const dateStr = formatDate(current);
    const day = current.getDay();
    if (day >= 1 && day <= 5) {
      const inBounds = (!joinDate || dateStr >= joinDate)
        && (!endDate || dateStr <= endDate);
      if (inBounds) eligibleDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  return Math.min(1, eligibleDays / totalWorkingDays);
}

/**
 * Returns completion status string based on logged vs contracted hours.
 * @param {number} logged
 * @param {number} contracted
 * @returns {'green'|'amber'|'red'}
 */
export function getCompletionStatus(logged, contracted) {
  if (contracted <= 0) return logged > 0 ? 'green' : 'amber';
  const pct = (logged / contracted) * 100;
  if (pct >= 95) return 'green';
  if (pct >= 75) return 'amber';
  return 'red';
}

/**
 * Returns completion percentage.
 * @param {number} logged
 * @param {number} contracted
 * @returns {number}
 */
export function getCompletionPct(logged, contracted) {
  if (contracted <= 0) return logged > 0 ? 100 : 0;
  return (logged / contracted) * 100;
}

/**
 * Maps completion status to BrioBadge variant.
 * @param {'green'|'amber'|'red'} status
 * @returns {string}
 */
export function statusToBadgeVariant(status) {
  if (status === 'green') return 'success';
  if (status === 'amber') return 'caution';
  return 'danger';
}

/**
 * Format duration in seconds to hours string with 1 decimal place.
 * @param {number} seconds
 * @returns {string}
 */
export function formatHoursFromSeconds(seconds) {
  if (!seconds || seconds === 0) return '0';
  const h = seconds / 3600;
  return h % 1 === 0 ? String(h) : h.toFixed(1);
}

/**
 * Format hours (already in hours) to string with 1 decimal place.
 * @param {number} hours
 * @returns {string}
 */
export function formatHours(hours) {
  if (!hours || hours === 0) return '0';
  return hours % 1 === 0 ? String(hours) : hours.toFixed(1);
}

/**
 * Format a variance value with + prefix for positive values.
 * @param {number} hours
 * @returns {string}
 */
export function formatVariance(hours) {
  if (Math.abs(hours) < 0.05) return '0';
  const formatted = hours % 1 === 0 ? String(hours) : hours.toFixed(1);
  return hours > 0 ? `+${formatted}` : formatted;
}

/**
 * Returns a date range { start, end } for a given preset key.
 * All dates are YYYY-MM-DD strings.
 * @param {string} preset
 * @returns {{ start: string, end: string }}
 */
export function getDatePresetRange(preset) {
  const today = new Date();
  const monday = getISOWeekMonday(today);
  const mondayDate = new Date(monday + 'T00:00:00');

  switch (preset) {
    case 'this-week': {
      const sun = new Date(mondayDate);
      sun.setDate(mondayDate.getDate() + 6);
      return { start: monday, end: formatDate(sun) };
    }
    case 'last-week': {
      const lastMon = new Date(mondayDate);
      lastMon.setDate(mondayDate.getDate() - 7);
      const lastSun = new Date(lastMon);
      lastSun.setDate(lastMon.getDate() + 6);
      return { start: formatDate(lastMon), end: formatDate(lastSun) };
    }
    case 'last-2-weeks': {
      const twoWeeksAgoMon = new Date(mondayDate);
      twoWeeksAgoMon.setDate(mondayDate.getDate() - 7);
      const sun = new Date(mondayDate);
      sun.setDate(mondayDate.getDate() + 6);
      return { start: formatDate(twoWeeksAgoMon), end: formatDate(sun) };
    }
    case 'this-month': {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return { start: formatDate(first), end: formatDate(last) };
    }
    case 'last-month': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatDate(first), end: formatDate(last) };
    }
    case 'last-3-months': {
      const first = new Date(today.getFullYear(), today.getMonth() - 3, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { start: formatDate(first), end: formatDate(last) };
    }
    case 'last-year': {
      const first = new Date(today.getFullYear() - 1, 0, 1);
      const last = new Date(today.getFullYear() - 1, 11, 31);
      return { start: formatDate(first), end: formatDate(last) };
    }
    default:
      // fallback to this week
      return getDatePresetRange('this-week');
  }
}

/**
 * Formats a Date to YYYY-MM-DD string.
 * @param {Date} d
 * @returns {string}
 */
export function formatDate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Builds column definitions for the weekly view day headers.
 * @param {string} weekMonday YYYY-MM-DD
 * @param {Set<string>} holidaySet
 * @returns {Array<{ date: string, label: string, dayLabel: string, isWeekend: boolean, isHoliday: boolean }>}
 */
export function buildWeekDayColumns(weekMonday, holidaySet) {
  const dates = getISOWeekDates(weekMonday);
  return dates.map((date, i) => ({
    date,
    label: `${DAY_LABELS[i]} ${parseInt(date.slice(8), 10)}`,
    dayLabel: DAY_LABELS[i],
    isWeekend: i >= 5,
    isHoliday: holidaySet.has(date),
  }));
}
